import type { OnboardingStepId } from "@aybuben/client/onboarding/steps.ts";
import { Suspense, type ReactNode } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { COL } from "../../theme.ts";
import { PICTURE } from "../home/picture-size.ts";
import { lazyPart, PartBoundary, usePartError } from "../lazy.tsx";
import { G } from "../primitives.tsx";
import { WIDTH } from "./art-size.ts";

// Lazy: the tree step draws the world art, which the startup bundle cannot carry. Every
// slide renders on mount, so its download starts with the welcome step, not on reaching it.
const treeArt = lazyPart(() => import("./tree-art.tsx").then((m) => m.TreeArt));

/** The tree art's box, empty: holds the slide's layout while the art loads, or if it cannot. */
const ArtBox = () => (
	<div
		className="mx-auto"
		style={{ width: WIDTH, height: (WIDTH * PICTURE.h) / PICTURE.w }}
		aria-hidden
	/>
);

export function StepArt({ id, active }: { id: OnboardingStepId; active: boolean }) {
	const onPartError = usePartError("onboarding-tree");
	switch (id) {
		case "welcome":
			return (
				<G size={120} style={{ color: COL.pom, lineHeight: 1 }}>
					Ա
				</G>
			);
		case "method":
			return <WordArt />;
		case "memory":
			return <MemoryArt />;
		case "tree":
			return (
				<PartBoundary fallback={<ArtBox />} onError={onPartError}>
					<Suspense fallback={<ArtBox />}>
						<treeArt.Part active={active} />
					</Suspense>
				</PartBoundary>
			);
	}
}

const Tile = ({ children, color = COL.pom }: { children: ReactNode; color?: string }) => (
	<span
		className="inline-flex h-12 w-11 items-center justify-center rounded-xl"
		style={{ background: COL.card, boxShadow: `0 3px 0 ${COL.line}` }}
	>
		<G size={26} style={{ color }}>
			{children}
		</G>
	</span>
);

function WordArt() {
	const t = useStrings();
	return (
		<div className="flex flex-col items-start" aria-hidden>
			<div className="flex gap-2">
				<Tile>ն</Tile>
				<Tile>ա</Tile>
			</div>
			<span className="ml-9 text-xl" style={{ color: COL.faint }}>
				↓
			</span>
			<G size={56} style={{ color: COL.pom }}>
				նա
			</G>
			<span className="text-sm italic" style={{ color: COL.mute }}>
				{t("onboarding.methodGloss")}
			</span>
		</div>
	);
}

function MemoryArt() {
	const t = useStrings();
	return (
		<div className="flex flex-col gap-4" aria-hidden>
			<div className="flex items-center gap-2 text-xs" style={{ color: COL.mute }}>
				<Tile color={COL.lapis}>տ</Tile>
				<span>{t("onboarding.memoryToday")}</span>
				<span style={{ color: COL.faint }}>→</span>
				<span style={{ opacity: 0.75 }}>
					<Tile color={COL.lapis}>տ</Tile>
				</span>
				<span>{t("onboarding.memoryLater")}</span>
			</div>
			<div className="flex items-center gap-2 text-xs" style={{ color: COL.mute }}>
				<span
					className="rounded-lg px-2.5 py-1 font-bold"
					style={{ background: COL.greenSoft, color: COL.greenDark }}
				>
					✓ {t("onboarding.memoryCheckpoint")}
				</span>
				<span>→ {t("onboarding.memoryNext")}</span>
			</div>
		</div>
	);
}
